import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RupiahInput } from "@/components/RupiahInput";
import { ImageUp, Loader2, Trash2, ClipboardPaste, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";

export const hasTransfer = (tx) => tx?.pay_status === "transfer" || (tx?.payments || []).some((p) => p.method === "transfer");

const transferTargets = (tx) => (tx.pay_status === "transfer"
  ? [{ id: "", label: "Transfer penuh", amount: tx.amount }]
  : (tx.payments || []).filter((p) => p.method === "transfer").map((p) => ({
    id: p.id, label: `${formatDate(p.date)} · ${p.is_initial ? (tx.pay_status === "kredit" ? "DP " : "Bagian ") : "Cicilan "}Transfer`, amount: p.amount,
  })));

const isImg = (name) => /\.(png|jpe?g|webp)$/i.test(name || "");

export function UploadTransferProofDialog({ tx, open, onOpenChange, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const name = file.name && file.name !== "image.png" ? file.name : `bukti-transfer-${Date.now()}.${(file.type.split("/")[1] || "png").replace("jpeg", "jpg")}`;
      const fd = new FormData();
      fd.append("file", file, name);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/transactions/${tx.id}/transfer-proofs`, { file_id: data.file_id, filename: data.filename });
      toast.success("Bukti transfer tersimpan.");
      onDone?.(); onOpenChange(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal upload"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
      if (item) { e.preventDefault(); upload(item.getAsFile()); } else toast.error("Clipboard tidak berisi gambar");
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pasteBtn = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith("image/"));
        if (type) { const blob = await it.getType(type); upload(new File([blob], "image.png", { type })); return; }
      }
      toast.error("Clipboard tidak berisi gambar");
    } catch { toast.error("Izin clipboard ditolak. Tekan Ctrl+V / tempel langsung di kotak ini."); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()} data-testid="tf-upload-dialog">
        <DialogHeader><DialogTitle>Upload Bukti Transfer · Nota {tx.nota_number}</DialogTitle></DialogHeader>
        <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="tf-upload-input" />
        <div contentEditable={!busy} suppressContentEditableWarning onInput={(e) => { e.currentTarget.innerHTML = ""; }}
          className="border-2 border-dashed rounded-xl p-6 text-center text-sm text-muted-foreground outline-none focus:border-brand caret-transparent" data-testid="tf-paste-zone">
          {busy ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand" /> : <>Tempel (Ctrl+V / tekan lama → Tempel) gambar bukti di sini</>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="tf-pick-file-btn"><ImageUp className="h-4 w-4 mr-1.5" /> Pilih Foto/Gambar</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={pasteBtn} data-testid="tf-paste-btn"><ClipboardPaste className="h-4 w-4 mr-1.5" /> Tempel dari Clipboard</Button>
        </div>
        <p className="text-xs text-muted-foreground">JPG, PNG, WEBP, atau PDF (maks. 10MB).</p>
      </DialogContent>
    </Dialog>
  );
}

function EditTransferAmountDialog({ tx, open, onOpenChange, onDone }) {
  const targets = transferTargets(tx);
  const [pid, setPid] = useState("");
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPid(targets[0]?.id || ""); setAmt(String(targets[0]?.amount || "")); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (id) => { setPid(id); setAmt(String(targets.find((t) => t.id === id)?.amount || "")); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/transactions/${tx.id}/transfer-amount`, { amount: Number(amt) || 0, payment_id: pid || null });
      toast.success("Nominal transfer diperbarui."); onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const split = tx.pay_status === "split";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf-edit-dialog">
        <DialogHeader><DialogTitle>Edit Nominal Transfer</DialogTitle></DialogHeader>
        {targets.length > 1 && (
          <div><Label>Pembayaran Transfer</Label>
            <select value={pid} onChange={(e) => pick(e.target.value)} className="mt-1.5 w-full h-10 rounded-md border px-3 text-sm" data-testid="tf-edit-target">
              {targets.map((t) => <option key={t.id} value={t.id}>{t.label} — {formatRupiah(t.amount)}</option>)}
            </select></div>
        )}
        <div><Label>Nominal Transfer Baru</Label><RupiahInput value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1.5 font-mono" data-testid="tf-edit-amount" /></div>
        <p className="text-xs text-muted-foreground" data-testid="tf-edit-note">
          {tx.pay_status === "transfer" ? "Jumlah tagihan ikut berubah." : split ? `Bagian Cash disesuaikan otomatis: ${formatRupiah(Math.max((tx.amount || 0) - (Number(amt) || 0), 0))} (total tetap ${formatRupiah(tx.amount)}).` : "Sisa tagihan menyesuaikan."} Perubahan tercatat otomatis.
        </p>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="tf-edit-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ProofThumb = ({ p, onDelete }) => (
  <div className="relative rounded-lg border overflow-hidden bg-secondary/40" data-testid={`tf-proof-${p.id}`}>
    <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer" data-testid={`tf-proof-link-${p.id}`}>
      {isImg(p.filename) ? <img src={fileUrl(p.file_id)} alt="Bukti transfer" className="h-32 w-full object-cover" />
        : <div className="h-32 flex items-center justify-center text-brand"><FileText className="h-8 w-8" /></div>}
    </a>
    <div className="px-2 py-1.5 text-[11px] text-muted-foreground">{p.uploaded_by} · {formatDate(p.uploaded_at)}</div>
    {onDelete && (
      <button type="button" onClick={() => onDelete(p.id)} className="absolute top-1.5 right-1.5 rounded-md bg-white/90 p-1 text-rose-600 hover:bg-white" data-testid={`tf-proof-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    )}
  </div>
);

export function TransferProofPanel({ tx, readOnly, onDone }) {
  const [up, setUp] = useState(false);
  const [ed, setEd] = useState(false);
  const proofs = tx.transfer_proofs || [];
  const edits = tx.transfer_edits || [];
  const del = async (pid) => {
    if (!window.confirm("Hapus bukti transfer ini?")) return;
    try { await api.delete(`/transactions/${tx.id}/transfer-proofs/${pid}`); toast.success("Bukti dihapus."); onDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="tf-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Bukti Transfer ({proofs.length})</h3>
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="border-brand/40 text-brand" onClick={() => setEd(true)} data-testid="tf-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Nominal Transfer</Button>
            <Button size="sm" className="bg-brand hover:bg-emerald-800" onClick={() => setUp(true)} data-testid="tf-upload-btn"><ImageUp className="h-3.5 w-3.5 mr-1.5" /> Upload Bukti Transfer</Button>
          </div>
        )}
      </div>
      {proofs.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="tf-empty">Belum ada bukti transfer.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{proofs.map((p) => <ProofThumb key={p.id} p={p} onDelete={readOnly ? null : del} />)}</div>
      )}
      {edits.length > 0 && (
        <div data-testid="tf-edit-history">
          <p className="text-xs font-mono uppercase text-muted-foreground mb-1.5">Riwayat Perubahan Nominal Transfer</p>
          <ul className="space-y-1 text-sm">
            {[...edits].reverse().map((e) => (
              <li key={e.id} className="flex flex-wrap justify-between gap-2 border-b py-1" data-testid={`tf-edit-${e.id}`}>
                <span className="font-mono">{formatRupiah(e.old)} → <b>{formatRupiah(e.new)}</b></span>
                <span className="text-xs text-muted-foreground">{e.by} · {formatDate(e.at)} {(e.at || "").slice(11, 16)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!readOnly && <UploadTransferProofDialog tx={tx} open={up} onOpenChange={setUp} onDone={onDone} />}
      {!readOnly && <EditTransferAmountDialog tx={tx} open={ed} onOpenChange={setEd} onDone={onDone} />}
    </Card>
  );
}
